// public/js/features/assets/asset-store.js
// Shared client-side asset cache with delta sync (docs/performance.md). Backed by
// IndexedDB so a page can render instantly from cache and then fetch only what
// changed since the last visit. All consumers (map, list) read from here instead
// of each re-fetching the whole fleet.
//
// Tenant scope (docs/tenant-scope.md): the cache is PARTITIONED per scope
// ('all' | 't:<tenantId>' | 'unassigned'). Each partition keeps its own rows and
// its own sync token, and the server load is narrowed with ?tenant= — switching
// back to an already-visited scope costs only its delta, the first visit a full
// load of that scope. A scope change resets the in-memory view; pages re-sync on
// the tenantScopeChanged event.
//
// Contract with the server (handlers/assets.mjs):
//   GET /assets?since=<token>&sv=<schemaVersion>[&tenant=<id|unassigned>]
//     → { assets:[changed|all], deleted:[ids], syncToken, schemaVersion, full }
//   `full:true` (first load, schema change, or identity change) ⇒ replace cache.
//   `full:false` ⇒ patch: upsert `assets`, drop `deleted`.
import {
  idbAvailable, idbGetScope, idbBulkPut, idbBulkDelete, idbClear, idbClearScope,
  idbGetMeta, idbSetMeta, idbClearMetaPrefix,
} from '../../utils/idb.js';
import { getCurrentUser } from '../../modules/rbac.js';
import { scopeKey, scopeTenantParam, SCOPE_CHANGE_EVENT } from '../../modules/tenant-scope.js';

const META_PREFIX = 'assetsSync';
const metaKey = (scope) => `${META_PREFIX}:${scope}`;

let _mem = [];
let _loadedScope = null;      // which scope partition _mem currently holds
const _subs = new Set();

function currentUserId() {
  try { return getCurrentUser()?.userId || getCurrentUser()?.sub || null; } catch { return null; }
}

function stripScope(rows) {
  return (rows || []).map(({ _scope, ...rest }) => rest);
}

function notify(changed, deleted) {
  for (const cb of _subs) { try { cb(_mem, { changed, deleted }); } catch (e) { console.warn('[asset-store] subscriber threw', e); } }
}

// Subscribe to post-sync updates. cb(assets, { changed:[ids], deleted:[ids] }).
export function subscribe(cb) { _subs.add(cb); return () => _subs.delete(cb); }

// Synchronous snapshot of the in-memory cache (may be empty before init()).
export function getCached() { return _mem; }

// A scope switch invalidates the in-memory view immediately (the partition on
// disk stays); consumers re-render from their own scope-change handlers.
if (typeof window !== 'undefined') {
  window.addEventListener(SCOPE_CHANGE_EVENT, () => { _mem = []; _loadedScope = null; });
}

// Load the ACTIVE scope's persisted partition into memory. Safe if IndexedDB is
// unavailable. Identity-guarded: a cache belonging to a DIFFERENT user (shared
// browser) is wiped — all partitions and tokens — rather than exposed.
export async function init() {
  const scope = scopeKey();
  if (_loadedScope === scope) return _mem;
  if (!idbAvailable()) { _loadedScope = scope; _mem = []; return _mem; }
  try {
    const meta = await idbGetMeta(metaKey(scope));
    const uid = currentUserId();
    if (meta && meta.userId && uid && meta.userId !== uid) {
      await idbClear('assets');
      await idbClearMetaPrefix(META_PREFIX);
      _mem = [];
    } else {
      _mem = stripScope(await idbGetScope('assets', scope));
    }
    _loadedScope = scope;
  } catch (e) { console.warn('[asset-store] idb load failed', e); _mem = []; _loadedScope = scope; }
  return _mem;
}

// Pull changes from the server and update the active scope's cache. Returns
// { assets, changed:[ids], deleted:[ids], full }. `force` requests a full reload.
// In-flight dedup: a non-forced sync started while another is already running
// returns the SAME promise — so an early prefetch and the map's own sync() share
// one request instead of firing two.
let _syncInFlight = null;
export async function sync({ force = false } = {}) {
  if (!force && _syncInFlight) return _syncInFlight;
  const p = _syncImpl({ force });
  if (!force) { _syncInFlight = p; p.finally(() => { if (_syncInFlight === p) _syncInFlight = null; }); }
  return p;
}

async function _syncImpl({ force = false } = {}) {
  const scope = scopeKey();
  await init();
  const uid = currentUserId();
  let meta = null;
  if (idbAvailable()) { try { meta = await idbGetMeta(metaKey(scope)); } catch {} }

  // Delta is only valid for the SAME user with a stored token + schema version.
  const identityOk = meta && meta.userId && meta.userId === uid;
  const canDelta = !force && identityOk && meta.syncToken != null && meta.schemaVersion != null;

  const params = new URLSearchParams();
  if (canDelta) { params.set('since', meta.syncToken); params.set('sv', meta.schemaVersion); }
  const tenantParam = scopeTenantParam();
  if (tenantParam) params.set('tenant', tenantParam);
  const qs = params.toString();
  const url = qs ? `api/v1/assets?${qs}` : 'api/v1/assets';

  const res = await window.authenticatedFetch(url);
  if (!res.ok) throw new Error(`Asset sync failed (${res.status})`);
  const data = await res.json();

  // The user switched scope while this request was in flight — its result
  // belongs to the OLD partition; drop it rather than corrupting the new view.
  if (scope !== scopeKey()) return { assets: _mem, changed: [], deleted: [], full: false, stale: true };

  const isFull = data.full !== false; // default to full when the flag is absent

  let changed = [];
  let deleted = data.deleted || [];

  if (isFull) {
    _mem = data.assets || [];
    changed = _mem.map(a => a.assetUUID);
    deleted = [];
    if (idbAvailable()) {
      try { await idbClearScope('assets', scope); await idbBulkPut('assets', _mem, scope); } catch (e) { console.warn('[asset-store] idb full write failed', e); }
    }
  } else {
    const byId = new Map(_mem.map(a => [a.assetUUID, a]));
    for (const a of (data.assets || [])) { byId.set(a.assetUUID, a); changed.push(a.assetUUID); }
    for (const id of deleted) byId.delete(id);
    _mem = [...byId.values()];
    if (idbAvailable()) {
      try {
        if (data.assets?.length) await idbBulkPut('assets', data.assets, scope);
        if (deleted.length) await idbBulkDelete('assets', deleted, scope);
      } catch (e) { console.warn('[asset-store] idb patch failed', e); }
    }
  }

  if (idbAvailable()) {
    try { await idbSetMeta(metaKey(scope), { syncToken: data.syncToken, schemaVersion: data.schemaVersion, userId: uid }); } catch {}
  }

  notify(changed, deleted);
  return { assets: _mem, changed, deleted, full: isFull };
}

// Drop everything (e.g. on logout) so the next user starts clean.
export async function clear() {
  _mem = []; _loadedScope = null;
  if (idbAvailable()) { try { await idbClear('assets'); await idbClearMetaPrefix(META_PREFIX); } catch {} }
}
